import './../input.scss';
import classnames from 'classnames';
import React, { useState, useRef } from 'react';
import { Icon } from '../../Icon/Icon';
import { IconName } from '../../Icon/IconName';
import { t } from '../../../locales/translate';
import { Spinner } from '../../Spinner/Spinner';
import { presence } from '../../../util/util';
import { FormField } from '../../../model/form';
import { formatNumber } from '../../../util/formatters';
import { Collapse } from '@nerdgeschoss/shimmer-component-collapse';

export interface FormFieldBase extends FormField<number | null> {
  label?: string;
  icon?: IconName;
  actions?: React.ReactNode | React.ReactNodeArray;
  autoFocus?: boolean;
  displayOptional?: boolean;
  loading?: boolean;
  step?: number;
}

interface Props extends FormFieldBase {
  textFieldRef?: React.MutableRefObject<HTMLInputElement | null>;
  min?: number;
  max?: number;
  disableBlurReset?: boolean;
}

export function FormNumberField({
  name,
  value,
  label,
  icon,
  actions,
  description,
  placeholder,
  autoFocus,
  required,
  disabled,
  readOnly,
  className,
  style,
  displayOptional = true,
  loading,
  inputId,
  errors,
  textFieldRef,
  step,
  onChange,
  onBlur,
  touched,
  valid,
  onFocus,
  min,
  max,
  disableBlurReset,
}: Props): JSX.Element {
  const [localValue, setLocalValue] = useState<string>();
  const [active, setActive] = useState(false);
  const displayErrors = touched && !valid;
  const full = active || placeholder || (value !== undefined && value !== null);

  const inputRef: React.MutableRefObject<HTMLInputElement | null> = useRef(null);
  const digits = step && step < 0.1 ? Math.log10(1 / step) : undefined;
  return (
    <div
      className={classnames('input', { 'input--with-icon': icon }, { 'input--disabled': disabled }, className)}
      style={style}
    >
      {icon && <Icon name={icon} className="input__icon" />}
      <div
        className={classnames('input__container', {
          'input__container--active': active,
          'input__container--full': full,
          'input__container--error': displayErrors,
        })}
      >
        <div className="input__input-wrapper">
          {label && (
            <label className="input__label" htmlFor={inputId}>
              {label}
              {displayOptional && !required && !disabled && ` (${t.general.optional})`}
            </label>
          )}
          <input
            id={inputId}
            name={name}
            className="input__input"
            value={localValue ?? (digits && value ? formatNumber(value, digits) : value) ?? ''}
            type="text"
            onChange={(v) => {
              if (readOnly) {
                v.preventDefault();
                return;
              }

              // this matches only numbers with optional decimal separator as dot
              const regexp = new RegExp(`^-?\\d*(\\.\\d{0,${digits}})?$`);

              if (!regexp.test(v.target.value.replace(',', '.'))) {
                return;
              }

              setLocalValue(v.target.value);

              if (presence(v.target.value)) {
                const numberValue = Number(v.target.value.replace(',', '.'));
                if (step === 1) {
                  return onChange?.(Math.round(numberValue));
                }
                return onChange?.(numberValue);
              }
              return onChange?.(null);
            }}
            onFocus={() => {
              setActive(true);
              onFocus?.();
            }}
            onBlur={() => {
              setActive(false);
              onBlur?.();

              if (!disableBlurReset) {
                setLocalValue(undefined);
              }
            }}
            step={step}
            autoFocus={autoFocus}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            ref={(ref) => {
              inputRef.current = ref;
              if (textFieldRef) {
                textFieldRef.current = ref;
              }
            }}
            min={min}
            max={max}
          />
          {actions && <div className="input__actions">{actions}</div>}
          {displayErrors && <Icon name="alert-circle--sm" className="input__alert-circle" />}
          {loading && <Spinner className="input__loading" color="var(--color-text-label)" scale="0.6" />}
        </div>
        <div className="input__description-wrapper">
          {description && <div className="input__description">{description}</div>}
          <Collapse open={displayErrors}>
            {errors?.map((error) => {
              return (
                <div className="input__error-message" key={error}>
                  {t.errors[error] || error}
                </div>
              );
            })}
          </Collapse>
        </div>
      </div>
    </div>
  );
}
